import React, { useState, useEffect, useMemo, useRef } from 'react'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import { ReasonIcons } from '../../../constants/Constants'
import ProgressBarAnalytics from './ProgressBarAnalytics'
import { student_reasons_list } from '../../../constants/StudentAnalyticsConstants'
import { emotionalWellbeingFactorsForSelectedStudentsList } from '../../alerts/constants/AlertsConstants'
import { loadClassReasonsData, loadCounsellorReasonsData, loadOrganizationReasonsData, loadStudentReasonsData } from '../../../server/server'
import { useHasStudentInPath, useQuery } from '../../../utils/util'
import { LoadingContainer } from './CheckInWidget'
import { useTranslation } from 'react-i18next'
import { calculateDateRange } from '../../../utils/dateUtils'
import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import InfoButton from '../../common/InfoButton'
import ExpandButton from '../../common/ExpandButton'

const SHOW_ROWS = 3

function ReasonsForEmotionsWidget({ daysFilter, isCounsellor, selectedUsersScope, analyticsType }) {
    const { t } = useTranslation()
    const query = useQuery()
    const isStudentAnalytics = useHasStudentInPath()
    const currentId = query.get('id')
    const requestVersion = useRef(0)
    const [limit, setLimit] = useState(SHOW_ROWS)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const [comfortableEmotions, setComfortableEmotions] = useState([])
    const [uncomfortableEmotions, setUncomfortableEmotions] = useState([])

    const totalMaxRows = useMemo(() => Math.max(comfortableEmotions?.total, uncomfortableEmotions?.total, 1), [comfortableEmotions, uncomfortableEmotions, data])

    // const totalMaxRows = useMemo(() => {
    //     const comfortableTotal = comfortableEmotions?.total ?? emotionalWellbeingFactorsForSelectedStudentsList.comfortable.total
    //     const uncomfortableTotal = uncomfortableEmotions?.total ?? emotionalWellbeingFactorsForSelectedStudentsList.uncomfortable.total
    //     return Math.max(comfortableTotal, uncomfortableTotal, 1)
    // }, [comfortableEmotions, uncomfortableEmotions, data])

    const showLoading = useMemo(() => loading || !data || (!data?.comfortable && !data?.uncomfortable), [loading, data])

    useEffect(() => {
        setLoading(true)
        loadData()
        return () => { requestVersion.current += 1 }
    }, [daysFilter, currentId, selectedUsersScope])

    const loadData = async () => {
        const version = ++requestVersion.current
        let callFunction
        switch (analyticsType) {
            case 'student':
                callFunction = loadStudentReasonsData
                break
            case 'year':
            case 'counsellor':
                callFunction = loadCounsellorReasonsData
                break
            case 'organization':
                callFunction = loadOrganizationReasonsData
                break
            default:
                callFunction = loadClassReasonsData
        }
        const { startDate, endDate } = calculateDateRange(daysFilter)
        const resp = await callFunction(isCounsellor ? selectedUsersScope : currentId, startDate, endDate)
        if (version !== requestVersion.current) return
        if (resp.status === 200) {
            setData(resp.data)
            setComfortableEmotions(resp.data?.comfortable)
            setUncomfortableEmotions(resp.data?.uncomfortable)
        }
        setLoading(false)
    }

    const handleShowMore = () => {
        if (limit === SHOW_ROWS) {
            setLimit(totalMaxRows)
        } else {
            setLimit(SHOW_ROWS)
        }
    }

    const getTitle = () => {
        if (isStudentAnalytics) {
            return t('Emotional Wellbeing Factors')
        } else if (isCounsellor) {
            return t('Counselor Emotional Wellbeing Factors')
        } else {
            return t('Class Emotional Wellbeing Factors')
        }
    }

    const getInfoText = () => {
        if (isStudentAnalytics) {
            return t('This section displays the most common reasons behind the comfortable and uncomfortable emotions submitted by the student during the selected period')
        } else if (isCounsellor) {
            return t('See how feeling factors are influencing students’ emotions.')
        } else {
            return t(
                'This section displays the factors contributing to the class’s comfortable and uncomfortable emotions. View the breakdown of each emotion type, along with the most common contributing factors for the selected period.'
            )
        }
    }
    return (
        <div className="bg-white rounded-[24px] p-6 mt-4" data-tour="emotional_wellbeing_factors">
            <div className=" flex flex-row items-center">
                <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{getTitle()}</p>
                <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px] ml-2">
                    <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                </div>
                <div className="flex-1"></div>
                {/* <button>
                    <InfoIcon color="#5E639B" />
                </button> */}
                <InfoButton text={getInfoText()} />
            </div>
            <div className="relative">
                <DataContainer
                    comfortableList={comfortableEmotions?.reasons ?? emotionalWellbeingFactorsForSelectedStudentsList.comfortable.reasons} //[]
                    uncomfortableList={uncomfortableEmotions?.reasons ?? emotionalWellbeingFactorsForSelectedStudentsList.uncomfortable.reasons} //[]
                    totalReasonsCountComfortable={comfortableEmotions?.total_emotions ?? emotionalWellbeingFactorsForSelectedStudentsList.comfortable.total_emotions} //0
                    totalReasonsCountUncomfortable={uncomfortableEmotions?.total_emotions ?? emotionalWellbeingFactorsForSelectedStudentsList.uncomfortable.total_emotions} //0
                    limit={limit}
                    blur={showLoading}
                    isStudentAnalytics={isStudentAnalytics}
                    isCounsellor={isCounsellor}
                />
                {showLoading && <LoadingContainer limit={limit} showEmptyData={!loading && !data} />}
            </div>

            <div className="flex flex-row justify-end w-full mt-2">
                {!loading && totalMaxRows > SHOW_ROWS && (
                    <button
                        className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full h-[34px] px-4 bg-[#F8F8FB]"
                        onClick={() => handleShowMore()}
                    >
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{limit > SHOW_ROWS ? t('Show less reasons') : t('Show more reasons')}</p>
                        <div className={`${limit > SHOW_ROWS ? 'rotate-180' : 'rotate-0'} transition`}>
                            <ChevronDownDuoIcon />
                        </div>
                    </button>
                )}
            </div>
        </div>
    )
}

export default ReasonsForEmotionsWidget

const DataContainer = ({ uncomfortableList, comfortableList, totalReasonsCountUncomfortable, totalReasonsCountComfortable, limit, blur, isStudentAnalytics, isCounsellor }) => {
    const { t } = useTranslation()
    return (
        <div className={`grid grid-cols-2 gap-2 mt-6 ${blur ? 'blur' : ''}`}>
            <div className="flex flex-col bg-[#F8F8FB] rounded-[16px] p-3 ">
                <div className="flex flex-row items-center">
                    <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#6FCC8733] ">
                        <Happy height={32} width={32} />
                    </div>
                    <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[10px]  flex-1">{t('Comfortable Emotions')}</p>
                    <p className="text-[#6FCC87] text-[16px] leading-5 font-semibold ml-2">{totalReasonsCountComfortable}</p>
                </div>
                <div className="w-full h-[1px] bg-[#ECEBF8] mt-[10px]"></div>
                <div className="flex flex-col gap-[10px] mt-[10px]">
                    {comfortableList.slice(0, limit).map((item, index) => {
                        const count = item.count
                        const reason = item.reason
                        const students = item.students
                        return (
                            <ReasonItem
                                key={index + item.reason}
                                reason={reason}
                                count={count}
                                progress={Math.round((count / totalReasonsCountComfortable) * 100)}
                                color={'#6FCC87'}
                                isStudentAnalytics={isStudentAnalytics}
                                students={students}
                                isCounsellor={isCounsellor}
                            />
                        )
                    })}
                </div>
            </div>
            <div className="flex flex-col bg-containerGray rounded-[16px] p-3 ">
                <div className="flex flex-row items-center">
                    <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#FC4C5933] ">
                        <Sad height={32} width={32} />
                    </div>
                    <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[10px]  flex-1">{t('Uncomfortable Emotions')}</p>
                    <p className="text-[#FC4C59] text-[16px] leading-5 font-semibold ml-2">{totalReasonsCountUncomfortable}</p>
                </div>
                <div className="w-full h-[1px] bg-[#ECEBF8] mt-[10px]"></div>
                <div className="flex flex-col gap-[10px] mt-[10px]">
                    {uncomfortableList.slice(0, limit).map((item, index) => {
                        const count = item.count
                        const reason = item.reason
                        const students = item.students
                        return (
                            <ReasonItem
                                key={index + item.reason}
                                reason={reason}
                                count={count}
                                progress={Math.round((count / totalReasonsCountUncomfortable) * 100)}
                                color={'#FD949B'}
                                isStudentAnalytics={isStudentAnalytics}
                                students={students}
                                isCounsellor={isCounsellor}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

const ReasonItem = ({ reason, count, progress, color, isStudentAnalytics, students, isCounsellor }) => {
    const { t } = useTranslation()

    return (
        <div>
            <div className="flex flex-row items-center justify-between flex-1">
                <div className="flex flex-row items-center">
                    <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#BEBECE33] ">{ReasonIcons(reason?.toLowerCase())}</div>

                    <p className="text-darkenPurple text-[14px] leading-5 font-medium ml-[10px]  w-[100px] capitalize">{t(reason)}</p>
                </div>
                <div className="flex flex-row items-center flex-1">
                    <div className="flex-1">
                        <ProgressBarAnalytics frontColor={`${color}`} restColor={`${color}60`} completed={progress} />
                    </div>
                    <p className={`text-darkenPurple text-[16px] leading-5 font-semibold ml-2 text-left ${isCounsellor ? 'w-[90px]' : 'w-[70px]'}`}>{`${count} (${progress}%)`}</p>
                </div>
                {/* {!isStudentAnalytics && (
                    <div className="ml-2">
                        <ExpandButton
                            open={open}
                            onClick={() => {
                                setOpen((prev) => !prev)
                            }}
                            width={32}
                            height={32}
                        />
                    </div>
                )} */}
            </div>

            <div className={`overflow-hidden transition-all ${'max-h-0'} ${'opacity-0'} mt-1`}>
                {students?.map((student, index) => (
                    <div className="flex flex-1 flex-row items-center justify-between h-8" key={index}>
                        <p className="text-darkenPurple text-[13px] leading-[18px] font-medium ml-[42px] ">{student.name}</p>
                        <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[42px] w-8 text-center">{student.number}</p>
                    </div>
                ))}
            </div>
        </div>
    )
}
